/**
 * 게임 카메라 시뮬레이터 — 데모 캔버스에 "실제 게임 화면에 보이는 영역"을 그리기 위한 순수 모델.
 *
 * 목적은 **기획자가 연출을 짐작할 수 있는 수준**이다. 정밀 재현이 아니다.
 *
 * 🔴 이름 충돌 주의: `script.js`의 전역 `zoom` / `viewOffset` / `isCameraTracking`은
 *    **에디터 뷰**의 축척·팬·추적이며 게임 카메라와 **무관**하다. 이 파일의 `ortho`가
 *    게임 카메라다. 둘을 섞으면 안 된다.
 *
 * ── 단위 ──────────────────────────────────────────────────────────────
 * **에디터 월드 단위 = Unity 월드 단위.** 양쪽이 같은 이동 공식을 쓴다:
 *   `multiplierConstant = 8 × 배속 × 0.05 = 0.4 × 배속`
 *   (Unity TrackGenerator / 에디터 `shared/utils.js:338` · `script.js:2061`)
 * 따라서 좌표 변환 없이 월드 단위로 계산해 기존 `p * zoom + viewOffset`에 그대로 태운다.
 *
 * ── 출처 ──────────────────────────────────────────────────────────────
 * 수치·동작은 Unity 클라이언트 저장소(Witch's Lounge) **코드·씬 파일 실측**을 클라 담당
 * 세션에게서 전달받은 것이다: `CameraEventController.cs` 전수 / `CameraTrailer.cs` /
 * `StageConstant.cs` / `Scene_TrackBase.unity`.
 *
 * ⚠️ **Play 모드 대조는 되지 않았다.** 코드에서 읽은 값이며 체감 일치는 미검증이다.
 *    어긋나면 아래 상수만 고치면 된다. Unity 코드는 이 저장소에 없다.
 */

// ── 클라이언트 실측 상수 ────────────────────────────────────────────────
// 체감이 어긋나면 여기만 고친다.

/** 기본 orthographic size (`LENS_ORTH_SIZE_DEFAULT`, 씬 실측값과 일치). */
export const DEFAULT_ORTHO = 60;

/** 화면비 16:9 고정 (FixedAspectLetterbox, 프로젝트 방침 D-22). */
export const CAMERA_ASPECT = 16 / 9;

/** `camera-zoom`의 distance 미지정 시 클라 기본값. */
export const DEFAULT_ZOOM_DISTANCE = 5.0;

/** `camera-focus_offset`의 offset 미지정 시 클라 기본값. */
export const DEFAULT_FOCUS_OFFSET = 0.5;

/**
 * 씬의 Cinemachine `PositionDamping` 실측값(x=0.3, y=0.3).
 *
 * ⚠️ 함정: **클라의 `camera-damping` 이벤트 기본값은 0.3이 아니라 0이다.**
 * 파라미터 없이 damping 이벤트를 넣으면 댐핑이 사라져 카메라가 딱 붙는다.
 * (리셋이 아니다.) 아래 EVENT_DEFAULT_DAMPING이 그 값이다.
 */
export const SCENE_DEFAULT_DAMPING = 0.3;

/** `camera-damping`의 multiplier 미지정 시 클라 기본값 — 씬 기본값과 다르다. */
export const EVENT_DEFAULT_DAMPING = 0;

/** `camera-rotate`의 isright 미지정 시 클라 기본값. */
export const DEFAULT_ROTATE_ISRIGHT = true;

// ── 파라미터 파싱 ───────────────────────────────────────────────────────
// 클라 규약: 키는 대소문자 무시 / 값이 없거나 파싱 실패하면 기본값 /
// bool은 bool.TryParse("true"/"false"), 그 외는 기본값.

/** eventParams 배열에서 이름으로 원시 값을 찾는다(대소문자 무시). */
export function rawParam(event, name) {
    if (!event || !Array.isArray(event.eventParams)) return undefined;
    const lowered = String(name).toLowerCase();
    const hit = event.eventParams.find(
        (p) => p && typeof p.paramName === 'string' && p.paramName.toLowerCase() === lowered
    );
    return hit ? hit.paramValue : undefined;
}

/** float 파라미터. 없거나 파싱 실패하면 fallback. */
export function floatParam(event, name, fallback = 0) {
    const raw = rawParam(event, name);
    if (raw === undefined || raw === null || raw === '') return fallback;
    const n = typeof raw === 'number' ? raw : parseFloat(raw);
    return Number.isFinite(n) ? n : fallback;
}

/** bool 파라미터. "true"/"false"만 인정하고 그 외에는 fallback(클라 bool.TryParse 규약). */
export function boolParam(event, name, fallback = false) {
    const raw = rawParam(event, name);
    if (typeof raw === 'boolean') return raw;
    if (raw === undefined || raw === null) return fallback;
    const s = String(raw).trim().toLowerCase();
    if (s === 'true') return true;
    if (s === 'false') return false;
    return fallback;
}

// ── 상태 ────────────────────────────────────────────────────────────────

/**
 * 카메라 초기 상태.
 *
 * `drawn`은 댐핑이 적용된 **실제 그려지는 중심**이고, 목표 중심과 구분된다.
 * `null`이면 아직 한 번도 안정화되지 않은 상태 — 첫 프레임에 목표로 스냅한다.
 */
export function createCameraState() {
    return {
        ortho: DEFAULT_ORTHO,
        rotation: 0,              // 도(度). 절대각이며 누적되지 않는다.
        isEvent: false,           // true면 캐릭터 추적 해제(위치 고정)
        eventPos: { x: 0, y: 0 }, // isEvent일 때의 고정 중심
        focusOffset: DEFAULT_FOCUS_OFFSET,
        damping: SCENE_DEFAULT_DAMPING,
        drawn: null,              // 댐핑 적용된 화면 중심
        tweens: { zoom: null, rotation: null, position: null }
    };
}

/**
 * fade 트윈을 건다. 같은 축의 기존 트윈은 **즉시 취소**되고,
 * zoom/rotation/position 3축은 서로를 취소하지 않는다.
 */
function setTween(state, axis, from, to, startTime, duration) {
    state.tweens[axis] = { from, to, startTime, duration };
}

/** 축 하나의 트윈을 현재 시각까지 진행시킨다. 선형 보간(의도적 근사). */
function sampleTween(tween, now) {
    if (!tween) return null;
    if (tween.duration <= 0) return { value: tween.to, done: true };
    const t = (now - tween.startTime) / tween.duration;
    if (t >= 1) return { value: tween.to, done: true };
    if (t <= 0) return { value: tween.from, done: false };
    const lerpNum = (a, b) => a + (b - a) * t;
    if (typeof tween.from === 'number') {
        return { value: lerpNum(tween.from, tween.to), done: false };
    }
    return {
        value: { x: lerpNum(tween.from.x, tween.to.x), y: lerpNum(tween.from.y, tween.to.y) },
        done: false
    };
}

// ── 이벤트 적용 ─────────────────────────────────────────────────────────

/**
 * 카메라 이벤트 하나를 상태에 적용한다.
 *
 * @param {object} state        createCameraState()로 만든 상태(제자리 변경)
 * @param {object} event        { eventType, eventId, eventTime, eventParams }
 * @param {object} ctx
 *   @param {number} ctx.speedMultiplier  배속
 *   @param {(t:number)=>{x,y}} ctx.positionAt  경로상 시각 t의 캐릭터 위치
 */
export function applyCameraEvent(state, event, ctx) {
    if (!event || event.eventType !== 'camera') return state;

    const speed = ctx.speedMultiplier || 1;
    const positionAt = ctx.positionAt;
    const now = event.eventTime;
    const fade = floatParam(event, 'fade', 0);

    switch (event.eventId) {
        case 'zoom': {
            // ★ distance가 곧 orthoSize다(거리가 아니다). 작을수록 확대.
            let target = floatParam(event, 'distance', DEFAULT_ZOOM_DISTANCE);
            if (boolParam(event, 'followspeed', false)) {
                target += target * ((speed - 1) / 2);
            }
            setTween(state, 'zoom', state.ortho, target, now, fade);
            break;
        }

        case 'rotate': {
            // ★★ 절대각이다. 누적되지 않는다 — angle=10을 두 번 넣어도 10도다.
            const angle = floatParam(event, 'angle', 0);
            const isRight = boolParam(event, 'isright', DEFAULT_ROTATE_ISRIGHT);
            setTween(state, 'rotation', state.rotation, isRight ? angle : -angle, now, fade);
            break;
        }

        case 'position': {
            // ★ 배속이 조건 없이 항상 곱해진다 → 배속을 바꾸면 좌표가 이동한다.
            const target = {
                x: floatParam(event, 'x', 0) * speed,
                y: floatParam(event, 'y', 0) * speed
            };
            beginPositionTween(state, target, now, fade, positionAt);
            break;
        }

        case 'position_from_character': {
            // ★★ 실행 "그 순간"의 캐릭터 위치를 한 번 스냅샷한 고정점이다.
            //    이름과 달리 캐릭터를 계속 따라가지 않는다.
            const base = positionAt(now);
            const useSpeed = boolParam(event, 'followspeed', false);
            const ox = floatParam(event, 'offset_x', 0) * (useSpeed ? speed : 1);
            const oy = floatParam(event, 'offset_y', 0) * (useSpeed ? speed : 1);
            beginPositionTween(state, { x: base.x + ox, y: base.y + oy }, now, fade, positionAt);
            break;
        }

        case 'position_from_time': {
            // ★ 0보다 큰 time만 유효. 하나도 없으면 아무 동작도 하지 않는다.
            const times = ['time1', 'time2', 'time3', 'time4']
                .map((k) => floatParam(event, k, 0))
                .filter((t) => t > 0);
            if (times.length === 0) break;

            const pts = times.map((t) => positionAt(t));
            const avg = pts.reduce(
                (acc, p) => ({ x: acc.x + p.x / pts.length, y: acc.y + p.y / pts.length }),
                { x: 0, y: 0 }
            );
            const useSpeed = boolParam(event, 'followspeed', false);
            const ox = floatParam(event, 'offset_x', 0) * (useSpeed ? speed : 1);
            const oy = floatParam(event, 'offset_y', 0) * (useSpeed ? speed : 1);
            beginPositionTween(state, { x: avg.x + ox, y: avg.y + oy }, now, fade, positionAt);
            break;
        }

        case 'position_off': {
            if (fade <= 0) {
                state.isEvent = false;
                state.tweens.position = null;
            } else {
                // fade 동안 고정점 → 실시간 캐릭터 추적 위치로 Lerp 후 해제.
                // 목표가 매 프레임 움직이므로 "따라가 붙는" 모양이 된다.
                state.tweens.position = {
                    from: currentCenter(state, now, positionAt),
                    to: null,          // null = 매 프레임 추적 위치를 목표로 삼는다
                    startTime: now,
                    duration: fade,
                    releasing: true
                };
            }
            break;
        }

        case 'focus_offset': {
            // 추적 중일 때만 의미가 있다(§3의 lead 거리).
            state.focusOffset = floatParam(event, 'offset', DEFAULT_FOCUS_OFFSET);
            break;
        }

        case 'damping': {
            // ⚠️ 기본값이 0이다. 파라미터를 비우면 댐핑이 사라진다(리셋이 아니다).
            state.damping = floatParam(event, 'multiplier', EVENT_DEFAULT_DAMPING);
            break;
        }

        case 'reset_camera': {
            // ★★ 위치는 건드리지 않는다. isEvent/고정위치/damping/focus_offset 모두 유지.
            //    고정을 풀려면 position_off를 따로 넣어야 한다.
            state.ortho = DEFAULT_ORTHO;
            state.rotation = 0;
            state.tweens.zoom = null;
            state.tweens.rotation = null;
            break;
        }

        default:
            // auto_focus 등 미구현 id는 클라에서도 아무 동작을 하지 않는다.
            break;
    }

    return state;
}

/** 위치 고정을 시작한다. fade가 있으면 현재 중심에서 목표로 보간한다. */
function beginPositionTween(state, target, now, fade, positionAt) {
    const from = currentCenter(state, now, positionAt);
    state.isEvent = true;
    state.eventPos = target;
    if (fade > 0) {
        setTween(state, 'position', from, target, now, fade);
    } else {
        state.tweens.position = null;
    }
}

/** 트윈을 무시한 "지금 이 순간의 목표 중심". */
function currentCenter(state, now, positionAt) {
    if (state.isEvent) return { x: state.eventPos.x, y: state.eventPos.y };
    return trackingCenter(state, now, positionAt, 1);
}

/**
 * 추적 중(isEvent=false)의 목표 중심.
 *
 * ★★ 캐릭터 위치가 아니라 **현재 위치와 조금 앞 위치의 중점**이다.
 *    코너에서 진행 방향으로 미리 쏠리는 이유가 이것이다.
 */
export function trackingCenter(state, time, positionAt, speedMultiplier = 1) {
    // lead의 단위는 **초**다(`CameraTrailer.cs:28` — PlayTime에 그대로 더해진다).
    // 배속으로 나누는 이유: 거리 = 속도 × 시간인데 속도가 배속에 비례하므로,
    // 시간을 배속으로 나누면 **내다보는 거리(유닛)가 배속과 무관하게 일정**해진다.
    const lead = state.focusOffset / (speedMultiplier || 1);
    const here = positionAt(time);
    const ahead = positionAt(time + lead);
    return { x: (here.x + ahead.x) / 2, y: (here.y + ahead.y) / 2 };
}

/**
 * 경로 조회 함수를 클라이언트의 clamp 규약에 맞게 감싼다.
 *
 * `StageUtils.GetPosition(float)` 실측: `time <= 0`이면 첫 노드, 모든 노드를
 * 지나치면 **마지막 노드에 고정**한다. 외삽은 하지 않는다.
 *
 * ★ 이 clamp가 곡 끝의 체감을 만든다 — `pos(t)`는 계속 전진하는데
 *   `pos(t + lead)`가 먼저 끝점에 붙어 굳으므로 중점이 끝점으로 수렴하고,
 *   결과적으로 **카메라가 서서히 멈춘다**. clamp를 빼면 이 감속이 사라진다.
 *
 * @param {(t:number)=>{x,y}} positionAt  원본 조회 함수
 * @param {number} startTime  경로 시작 시각(보통 0)
 * @param {number} endTime    경로 끝 시각. 미지정이면 끝 clamp를 하지 않는다.
 */
export function clampedPositionAt(positionAt, startTime = 0, endTime = Infinity) {
    return (t) => {
        if (t <= startTime) return positionAt(startTime);
        if (t >= endTime) return positionAt(endTime);
        return positionAt(t);
    };
}

// ── 시각 단위 계산 ──────────────────────────────────────────────────────

/**
 * 시각 `time`에서의 카메라를 계산한다.
 *
 * 🔴 **시크(seek) 대응**: 카메라는 노트와 달리 *상태*라서, 중간으로 뛰어들면
 * 이전 이벤트가 실행되지 않아 상태가 틀어진다(뒤로 감았는데 줌은 그대로).
 * 그래서 매번 **0부터 대상 시각까지 카메라 이벤트를 다시 훑어 재구성**한다.
 * 카메라 이벤트는 수가 적어 비용이 무시할 만하다.
 *
 * @param {Array}  events  전체 이벤트 목록(카메라 외 타입이 섞여 있어도 된다)
 * @param {number} time    대상 시각(초, eventTime과 같은 축)
 * @param {object} ctx     { speedMultiplier, positionAt, prev?, dt? }
 *   `prev`/`dt`를 주면 댐핑을 이어서 적용한다(재생 중 프레임 갱신용).
 * @returns {{center:{x,y}, ortho:number, rotation:number, isEvent:boolean,
 *            halfWidth:number, halfHeight:number, state:object}}
 */
export function cameraAt(events, time, ctx) {
    const speed = ctx.speedMultiplier || 1;
    const positionAt = ctx.positionAt;

    const state = createCameraState();

    const cameraEvents = (events || [])
        .filter((e) => e && e.eventType === 'camera' && typeof e.eventTime === 'number')
        .filter((e) => e.eventTime <= time)
        .sort((a, b) => a.eventTime - b.eventTime);

    for (const ev of cameraEvents) {
        settleTweens(state, ev.eventTime, positionAt, speed);
        applyCameraEvent(state, ev, { speedMultiplier: speed, positionAt });
    }

    settleTweens(state, time, positionAt, speed);

    // 목표 중심
    const target = state.isEvent
        ? { x: state.eventPos.x, y: state.eventPos.y }
        : trackingCenter(state, time, positionAt, speed);

    // 진행 중인 위치 트윈이 있으면 그 값이 우선한다.
    let center = target;
    const posTween = state.tweens.position;
    if (posTween) {
        if (posTween.releasing) {
            const live = trackingCenter(state, time, positionAt, speed);
            const t = posTween.duration > 0
                ? Math.min(1, Math.max(0, (time - posTween.startTime) / posTween.duration))
                : 1;
            center = {
                x: posTween.from.x + (live.x - posTween.from.x) * t,
                y: posTween.from.y + (live.y - posTween.from.y) * t
            };
        } else {
            const s = sampleTween(posTween, time);
            if (s) center = s.value;
        }
    }

    // 댐핑 — 지수 감쇠 한 줄 근사(Cinemachine 내부와 동일하진 않은 의도적 근사).
    // ★ 위치를 고정해도 댐핑은 계속 작동한다.
    //
    // 체감이 어긋나면 **수식을 바꾸기 전에 SCENE_DEFAULT_DAMPING 계수부터 조절**할 것
    // (0.3 → 0.2~0.5). Cinemachine도 개념상 "목표에 지수적으로 수렴"이라 큰 틀은 같고,
    // 근사 오차는 대개 "시간상수가 조금 다르다"로 나타나 계수 하나로 흡수된다.
    // 차이가 드러난다면 급격한 전환 직후 1~2프레임(position 이벤트로 순간이동시킬 때)이다.
    let drawn = center;
    if (ctx.prev && ctx.prev.drawn && typeof ctx.dt === 'number' && ctx.dt > 0) {
        const d = state.damping;
        const k = d > 0 ? 1 - Math.exp(-ctx.dt / d) : 1;
        drawn = {
            x: ctx.prev.drawn.x + (center.x - ctx.prev.drawn.x) * k,
            y: ctx.prev.drawn.y + (center.y - ctx.prev.drawn.y) * k
        };
    }
    state.drawn = drawn;

    return {
        center: drawn,
        ortho: state.ortho,
        rotation: state.rotation,
        isEvent: state.isEvent,
        halfHeight: state.ortho,
        halfWidth: state.ortho * CAMERA_ASPECT,
        state
    };
}

/** 진행 중인 zoom/rotation 트윈을 시각 `now`까지 확정한다. */
function settleTweens(state, now, positionAt, speed) {
    const z = sampleTween(state.tweens.zoom, now);
    if (z) {
        state.ortho = z.value;
        if (z.done) state.tweens.zoom = null;
    }
    const r = sampleTween(state.tweens.rotation, now);
    if (r) {
        state.rotation = r.value;
        if (r.done) state.tweens.rotation = null;
    }
    const p = state.tweens.position;
    if (p && p.releasing) {
        if (p.duration <= 0 || now - p.startTime >= p.duration) {
            state.isEvent = false;
            state.tweens.position = null;
        }
    } else if (p) {
        const s = sampleTween(p, now);
        if (s && s.done) {
            state.eventPos = s.value;
            state.tweens.position = null;
        }
    }
}

/**
 * 카메라 사각형의 네 모서리를 **월드 좌표**로 반환한다(회전 반영).
 *
 * 반환 순서는 좌상 → 우상 → 우하 → 좌하.
 *
 * 🔴 회전 부호: Unity 2D에서 양수 Z는 반시계지만, 캔버스는 Y축이 아래로
 * 뒤집혀 있어 그대로 쓰면 화면상 방향이 반대로 보인다. 그래서 여기서 부호를 뒤집는다.
 */
export function cameraCorners(cam) {
    const { center, halfWidth, halfHeight } = cam;
    const rad = (-cam.rotation * Math.PI) / 180; // ← Y축 반전 보정
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    return [
        [-halfWidth, -halfHeight],
        [halfWidth, -halfHeight],
        [halfWidth, halfHeight],
        [-halfWidth, halfHeight]
    ].map(([dx, dy]) => ({
        x: center.x + dx * cos - dy * sin,
        y: center.y + dx * sin + dy * cos
    }));
}
